import type { ReactNode } from 'react';

import { AssessmentOutlined, Close } from '@mui/icons-material';
import {
	Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography,
} from '@mui/material';

import { AuditRecordModel } from '@@api';
import JsonDataDisplay from '../../common/components/JsonDataDisplay';

const utcDateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'medium', timeZone: 'UTC' });
const mono = 'ui-monospace, Consolas, monospace';

export function formatAuditTimestamp(value: string) {
	return utcDateTime.format(new Date(value));
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
	return (
		<Box sx={{ minWidth: 0 }}>
			<Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
			<Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{children}</Typography>
		</Box>
	);
}

export default function AuditDetailDialog({ record, onClose }: { record: AuditRecordModel | null; onClose: () => void }) {
	const attributes = Object.entries(record?.attributes ?? {});

	return (
		<Dialog open={!!record} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="audit-detail-title">
			{record && <>
				<Box sx={{ px: 3, py: 2.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
					<Box sx={{ display: 'grid', placeItems: 'center', p: 1, borderRadius: 1.5, bgcolor: 'action.selected', color: 'primary.main' }}>
						<AssessmentOutlined aria-hidden="true" />
					</Box>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="caption" color="text.secondary">Audit record</Typography>
						<DialogTitle id="audit-detail-title" sx={{ p: 0, mb: 1, overflowWrap: 'anywhere' }}>{record.actionName}</DialogTitle>
						<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
							<Chip size="small" color="primary" variant="outlined" label={record.type} />
							{record.source && <Chip size="small" variant="outlined" label={record.source} />}
						</Stack>
					</Box>
					<IconButton size="small" aria-label="Close audit details" onClick={onClose}><Close fontSize="small" /></IconButton>
				</Box>

				<DialogContent dividers>
					<Stack spacing={2.5}>
						<Box>
							<Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>Record details</Typography>
							<Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
								<Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
									<DetailField label="Identity">
										{record.identityName}
										<Box component="span" sx={{ display: 'block', fontFamily: mono, color: 'text.secondary', fontSize: '0.75rem' }}>{record.identityId}</Box>
									</DetailField>
									<DetailField label="Timestamp (UTC)">{formatAuditTimestamp(record.timestamp)}</DetailField>
									<DetailField label="Inserted (UTC)">{formatAuditTimestamp(record.createdDate)}</DetailField>
									<DetailField label="Updated (UTC)">{formatAuditTimestamp(record.updatedDate)}</DetailField>
									<DetailField label="Record ID"><Box component="span" sx={{ fontFamily: mono }}>{record.id}</Box></DetailField>
								</Box>
							</Paper>
						</Box>

						<Box>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
								<Typography component="h3" variant="subtitle2">Attributes</Typography>
								<Chip size="small" label={attributes.length} />
							</Stack>
							{attributes.length > 0 ? (
								<Paper variant="outlined" component="dl" sx={{ m: 0, overflow: 'hidden' }}>
									{attributes.map(([key, value], index) => (
										<Box key={key} sx={{ px: 2, py: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 2fr)' }, columnGap: 2, rowGap: 0.25, borderTop: index ? 1 : 0, borderColor: 'divider', overflowWrap: 'anywhere' }}>
											<Typography component="dt" variant="body2" color="text.secondary">{key}</Typography>
											<Typography component="dd" variant="body2" sx={{ m: 0, fontFamily: mono }}>{value === '' ? '""' : value}</Typography>
										</Box>
									))}
								</Paper>
							) : <Typography variant="body2" color="text.secondary">No attributes recorded.</Typography>}
						</Box>

						{record.payload !== undefined ? (
							<Paper variant="outlined">
								<JsonDataDisplay title="Payload" data={record.payload} asAccordion defaultExpanded showEmpty
									accordionSubtitle="JSON" settingsKey="auditDetail.payload" />
							</Paper>
						) : (
							<Box>
								<Typography component="h3" variant="subtitle2">Payload</Typography>
								<Typography variant="body2" color="text.secondary">No payload recorded.</Typography>
							</Box>
						)}
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 1.5 }}><Button onClick={onClose}>Close</Button></DialogActions>
			</>}
		</Dialog>
	);
}
